import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HTTP_INTERCEPTORS, HttpErrorResponse } from '@angular/common/http';
import { Observable, Subject, combineLatest, of, throwError } from 'rxjs';
import { Store } from '@ngrx/store';
import { map, mergeMap, catchError, first, take, switchMap, finalize } from 'rxjs/operators';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { languageSelector } from 'src/app/global/store/global.selectors';
import { DEFAULT_LANG } from 'src/config/lang.config';
import { State } from '../../store';
import { isAuthenticatedSelector, authConfigIdSelector, authOrgSelector } from '../store/auth.selectors';
import { LogoutAction } from '../store/auth.actions';

@Injectable({ providedIn: 'root' })
export class AuthInterceptor implements HttpInterceptor {
    private refreshInProgress = false;
    private refreshResult$ = new Subject<string | null>();

    constructor(
        private store: Store<State>,
        private oidcService: OidcSecurityService,
    ) {}

    public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return this.getAuthRequest(req).pipe(
            mergeMap((r) => next.handle(r)),
            catchError((err) => {
                if (err instanceof HttpErrorResponse && err.status === 401) {
                    return this.handleUnauthorized(req, next, err);
                }
                return throwError(err);
            }),
        );
    }

    private handleUnauthorized(req: HttpRequest<any>, next: HttpHandler, err: HttpErrorResponse): Observable<HttpEvent<any>> {
        if (this.refreshInProgress) {
            // Another request is already refreshing — wait for it
            return this.refreshResult$.pipe(
                take(1),
                switchMap((accessToken) => {
                    if (!accessToken) {
                        return throwError(() => err);
                    }
                    const retried = req.clone({
                        headers: req.headers.set('Authorization', `Bearer ${accessToken}`),
                    });
                    return next.handle(retried);
                }),
            );
        }

        // This request is the first to hit 401 — it owns the refresh
        this.refreshInProgress = true;

        return this.store.select(authConfigIdSelector).pipe(
            take(1),
            switchMap((configId) =>
                this.oidcService.forceRefreshSession(null, configId).pipe(
                    catchError((_refreshErr) => {
                        this.refreshResult$.next(null);
                        this.store.dispatch(LogoutAction());
                        return throwError(() => err);
                    }),
                ),
            ),
            switchMap(({ accessToken }) => {
                if (!accessToken) {
                    this.refreshResult$.next(null);
                    this.store.dispatch(LogoutAction());
                    return throwError(() => err);
                }
                this.refreshResult$.next(accessToken);
                const retried = req.clone({
                    headers: req.headers.set('Authorization', `Bearer ${accessToken}`),
                });
                return next.handle(retried);
            }),
            finalize(() => {
                this.refreshInProgress = false;
            }),
        );
    }

    private getAuthRequest(req: HttpRequest<any>): Observable<HttpRequest<any>> {
        const skipAuth = req.headers.get('x-ignore-auth') === 'true';

        return this.store.select(isAuthenticatedSelector).pipe(
            first(),
            // Step 1: Attach Bearer token if authenticated
            switchMap((isAuth) => {
                if (!isAuth || skipAuth) {
                    return of(req);
                }
                return this.store.select(authConfigIdSelector).pipe(
                    take(1),
                    switchMap((configId) => this.oidcService.getAccessToken(configId)),
                    take(1),
                    map((token) => {
                        if (!token || req.headers.get('Authorization')) {
                            return req;
                        }
                        return req.clone({
                            headers: req.headers.set('Authorization', `Bearer ${token}`),
                        });
                    }),
                );
            }),
            // Step 2: Attach common headers (lang, org, app)
            switchMap((r) =>
                combineLatest([this.store.select(languageSelector).pipe(take(1)), this.store.select(authOrgSelector).pipe(take(1))]).pipe(
                    map(([lang, orgTag]) => {
                        let headers = r.headers.set('X-Application', 'admin').set('Accept-Language', lang ?? DEFAULT_LANG);

                        if (orgTag && !r.headers.has('x-organisation')) {
                            headers = headers.set('x-organisation', orgTag);
                        }

                        return r.clone({ headers });
                    }),
                ),
            ),
        );
    }
}

export const AUTH_INTERCEPTOR_PROVIDER = {
    provide: HTTP_INTERCEPTORS,
    useClass: AuthInterceptor,
    multi: true,
};
